import { QueryKeys } from '@/enums/index';
import { readGuestCart, syncGuestCart } from '@/helpers/guest-cart-helper';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import { IAddToCartItem, IAddToCartItemAttribute, ICartItems, IGetCartProduct } from '@/interfaces/cart';
import { addToCart, deleteFromCart, editCartItemQuantity, getCart } from '@/services/cart.service';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { getDataLayer } from '@/utils/data-layer';

export const useCart = (queryClient: QueryClient, session?: INextAuthSessionModel | null, storeIds?: string[]) => {
  // Build query key array
  const queryKey = [QueryKeys.cartItems, session?.accessToken || 'anonymous', storeIds ? storeIds.join(',') : ''];

  const query = useQuery<IGetCartProduct>({
    queryKey,
    queryFn: async () => {
      if (session) {
        //  return await get401();
        return await getCart(session, storeIds);
      } else {
        const localCart = readGuestCart();
        if (!localCart.Items.length) return localCart;

        try {
          return await syncGuestCart(storeIds);
        } catch {
          console.log('cart sync failed, returning cart from local storage as is.');
          return localCart;
        }
      }
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData) => previousData, // keepPreviousData was renamed to placeholderData in v5
    gcTime: 1000 * 60 * 5, // cacheTime was renamed to gcTime in v5 (5 minutes)
    enabled: true, // Explicitly enable the query
  });
  return query;
}

export const useAddToCartMutation = (addToCartCallback?: (shouldOpenCart?: boolean, shouldOpenNotification?: boolean) => void) => {
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  return useMutation({
    mutationFn: (cartItem: ICartItems) => {
      if (authObject.isAuthenticated) {
        let tempAttributes: IAddToCartItemAttribute[] = [];
        let selectedVariant = cartItem.Variations.find((x) => x.IsSelected);
        if (selectedVariant) {
          tempAttributes.push({
            key: selectedVariant.ProductAttributeId,
            value: selectedVariant.ProductAttributeValueId,
          });
        }
        const payload: IAddToCartItem = {
          productId: cartItem.ProductId,
          quantity: cartItem.Quantity,
          attributes: tempAttributes,
        };
        return addToCart(payload);
      } else {
        let cartItems: IGetCartProduct = {
          Items: [],
          SubTotalValue: 0,
          TotalValue: 0,
          Warnings: [],
        };
        let stringifiedCartItems = localStorage.getItem('cart');
        if (stringifiedCartItems) {
          // update empty cart Object with Cart Items form local storage
          try {
            let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;
            if (tempCartItems.Items) {
              cartItems = tempCartItems;
            } else {
              throw Error('parsed json is corrupted');
            }
          } catch (e) {
            console.log(e, 'json parse error');
          }
        }
        let foundProduct = cartItems.Items.find((x) => x.Id === cartItem.Id);
        if (foundProduct) {
          foundProduct.Quantity += cartItem.Quantity;
        } else {
          cartItems.Items.push(cartItem);
        }
        cartItems.SubTotalValue += cartItem.UnitPriceValue * cartItem.Quantity;
        cartItems.TotalValue += cartItem.UnitPriceValue * cartItem.Quantity;

        localStorage.setItem('cart', JSON.stringify(cartItems));

        return new Promise((resolve) => resolve('Cart updated locally.'));
      }
    },
    onSuccess: (data, cartItem: ICartItems) => {
      addToCartCallback?.(cartItem.shouldOpenCart, cartItem.shouldOpenNotification);
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous'] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
      let obj: { [key: string]: string } = {};
      if (cartItem?.Category) {
        let splittedCategories = cartItem?.Category.split(',');
        splittedCategories.forEach((category, categoryIndex) => {
          obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
        });
      }
      if (!cartItem?.IsGiftVoucher && cartItem?.Gender) {
        obj['gender'] = cartItem?.Gender;
      }

      const dataLayer = getDataLayer();
      if (dataLayer) {
        dataLayer.push({ ecommerce: null });

        dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'BDT',
            value: cartItem?.SubTotalValue,
            items: [
              {
                item_id: cartItem?.ProductSeName,
                item_name: cartItem?.ProductName,
                item_sku: cartItem?.ProductSku,
                price: cartItem?.SubTotalValue,
                quantity: cartItem?.Quantity,
                item_variant: cartItem.Variations?.find((x) => x.IsSelected)?.VariantSku,
                color: cartItem?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
                size: cartItem.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
                item_brand: cartItem?.Brand,
                ...obj,
              },
            ],
          },
        });
      }
    },
    onError: (data: any) => {
      openNotification('danger', 'Add to cart failed.', data.data.Messages[0]);
      // this is to update the selected quantity
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous'] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
    },
  });
};

export const useEditCartProductMutation = (editCartProductCallback: () => void) => {
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  return useMutation({
    mutationFn: (cartItem: ICartItems) => {
      if (authObject.isAuthenticated) {
        return editCartItemQuantity({ shoppingCartItemId: cartItem.Id, quantity: cartItem.Quantity });
      } else {
        let stringifiedCartItems = localStorage.getItem('cart');
        if (stringifiedCartItems) {
          try {
            let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;
            if (tempCartItems.Items) {
              let cartItems: IGetCartProduct = tempCartItems;
              let foundProduct = cartItems.Items.find((x) => x.Id === cartItem.Id);
              if (foundProduct) {
                foundProduct.Quantity = cartItem.Quantity;
                cartItems.SubTotalValue = 0;
                cartItems.TotalValue = 0;
                cartItems.Items.forEach((tempCartItem) => {
                  cartItems.SubTotalValue += tempCartItem.UnitPriceValue * tempCartItem.Quantity;
                  cartItems.TotalValue += tempCartItem.UnitPriceValue * tempCartItem.Quantity;
                });
              }
              localStorage.setItem('cart', JSON.stringify(cartItems));
            } else {
              throw Error('parsed json is corrupted');
            }
          } catch (e) {
            console.log(e, 'json parse error');
          }
        }
        return new Promise((resolve) => resolve('Cart updated locally.'));
      }
    },
    onSuccess: (data) => {
      editCartProductCallback();
      openNotification('success', 'Cart', 'Product quantity updated Successfully');
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous'] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
    },
    onError: (data: any) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous'] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
      openNotification('danger', 'Cart', data.data.Messages[0]);
    },
  });
};

export const useDeleteCartProduct = () => {
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  return useMutation({
    mutationFn: (cartItem: ICartItems) => {
      console.log(cartItem);

      if (!authObject.isAuthenticated) {
        let stringifiedCartItems = localStorage.getItem('cart');
        if (stringifiedCartItems) {
          try {
            let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;
            if (tempCartItems.Items) {
              let foundProductIndex = tempCartItems.Items.findIndex((x) => x.Id === cartItem.Id);
              if (foundProductIndex > -1) {
                tempCartItems.SubTotalValue -=
                  tempCartItems.Items[foundProductIndex].UnitPriceValue * tempCartItems.Items[foundProductIndex].Quantity;
                tempCartItems.TotalValue -= tempCartItems.Items[foundProductIndex].UnitPriceValue * tempCartItems.Items[foundProductIndex].Quantity;
                tempCartItems.Items.splice(foundProductIndex, 1);
                localStorage.setItem('cart', JSON.stringify(tempCartItems));
              }
              let obj: { [key: string]: string } = {};
              if (cartItem?.Category) {
                let splittedCategories = cartItem?.Category.split(',');
                splittedCategories.forEach((category, categoryIndex) => {
                  obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
                });
              }
              if (!cartItem?.IsGiftVoucher && cartItem?.Gender) {
                obj['gender'] = cartItem.Gender;
              }
              const dataLayer = getDataLayer();
              if (dataLayer) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                  event: 'remove_from_cart',
                  ecommerce: {
                    currency: 'BDT',
                    value: cartItem?.SubTotalValue,
                    items: [
                      {
                        item_id: cartItem?.ProductSeName,
                        item_name: cartItem?.ProductName,
                        item_sku: cartItem?.ProductSku,
                        price: cartItem?.SubTotalValue,
                        quantity: cartItem?.Quantity,
                        item_variant: cartItem.Variations?.find((x) => x.IsSelected)?.VariantSku,
                        color: cartItem?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
                        size: cartItem.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
                        item_brand: cartItem?.Brand,
                        ...obj,
                      },
                    ],
                  },
                });
              }
              return Promise.resolve('Cart updated successfully.');
            } else {
              throw new Error('parsed json is corrupted');
            }
          } catch (e) {
            console.log(e, 'json parse error');
            return Promise.reject(new Error('Cart updated failed.'));
          }
        } else {
          return Promise.reject(new Error('Item not found.'));
        }
      } else {
        let obj: { [key: string]: string } = {};
        if (cartItem?.Category) {
          let splittedCategories = cartItem?.Category.split(',');
          splittedCategories.forEach((category, categoryIndex) => {
            obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
          });
        }
        if (!cartItem?.IsGiftVoucher && cartItem?.Gender) {
          obj['gender'] = cartItem?.Gender;
        }
        const dataLayer = getDataLayer();
        if (dataLayer) {
          dataLayer.push({ ecommerce: null });
          dataLayer.push({
            event: 'remove_from_cart',
            ecommerce: {
              currency: 'BDT',
              value: cartItem?.SubTotalValue,
              items: [
                {
                  item_id: cartItem?.ProductSeName,
                  item_name: cartItem?.ProductName,
                  item_sku: cartItem?.ProductSku,
                  price: cartItem?.SubTotalValue,
                  quantity: cartItem?.Quantity,
                  item_variant: cartItem.Variations?.find((x) => x.IsSelected)?.VariantSku,
                  color: cartItem?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
                  size: cartItem.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
                  item_brand: cartItem?.Brand,
                  ...obj,
                },
              ],
            },
          });
        }
        return deleteFromCart(cartItem.Id);
      }
    },
    onSuccess: (cartItem: ICartItems) => {
      openNotification('success', 'Cart', 'Product Deleted from Cart Successfully');
      queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous'] });
    },
    onError: (data: any) => {
      openNotification('danger', 'Delete cart item failed', data.data.Messages[0]);
    },
  });
};
